import { useEffect, useState, useMemo, useRef } from 'react';
import { join } from 'light-component';
import { Select as ASelect, Tag, Space } from 'antd';
import { useBreakpoint } from '../../../../hooks';
import { t } from '../../../i18n';
import styles from '../style.module.less';
import { useDeepCompareMemoize } from './useDeepCompareMemoize';
import type { ReactElement } from 'react';
import type { SelectProps as ASelectProps } from 'antd';
import type { SelectOption, SelectProps } from '../types';

const SELECT_ALL = 'all';

export function Select(props: SelectProps): ReactElement {
  const {
    disabled = false,
    value,
    loading = false,
    options,
    placeholder,
    onChange,
    labelRender,
    maxTagCount = 1,
    allowClear = false
  } = props;

  const selectRef = useRef<any>(null);
  const [minWidth, setMinWidth] = useState(undefined);
  const { md } = useBreakpoint();
  const [selected, setSelected] = useState<string[]>([]);
  const [keyWords, setKeyWords] = useState('');

  const optionsMemo = useDeepCompareMemoize(options);
  const selectOptions = useMemo(() => {
    if (options.length > 0) {
      return [{ value: SELECT_ALL, label: t('全选') }, ...options];
    }
    return [];
  }, [optionsMemo]);

  const optionRender = useMemo(() => (option => (
    <Space>
      {option.data.tag != null && <Tag>{option.data.tag}</Tag>}
      {option.data.label}
    </Space>
  )) as ASelectProps['optionRender'], []);

  const onSearch = (val: string): void => {
    setKeyWords(val);
  };

  const filterOptions = useMemo(() => (
    [...options.filter(option => filterOption(keyWords, option))]
  ), [keyWords, optionsMemo]);

  useEffect(() => {
    const realValue = selected.filter(item => item !== SELECT_ALL);
    if (!Array.isArray(value) && realValue.length > 0) {
      setSelected([]);
      return;
    }
    if (Array.isArray(value) && (value.length !== realValue.length
      || (value as string[]).some((item: string, index: number) => item !== realValue[index])
    )) {
      if (value.length === options.length && options.length > 0) {
        setSelected([SELECT_ALL, ...value]);
      } else {
        setSelected(value);
      }
    }
  }, [value]);

  useEffect(() => {
    if (!selected.includes(SELECT_ALL)) {
      if (filterOptions.map(item => item.value).every(item => selected.includes(item)) && filterOptions.length > 0) {
        setSelected([SELECT_ALL, ...selected]);
      }
    } else {
      if (filterOptions.map(item => item.value).some(item => !selected.includes(item))) {
        setSelected(selected.filter(item => item !== SELECT_ALL));
      }
    }
  }, [keyWords]);

  useEffect(() => {
    const root = selectRef.current?.nativeElement;
    if (root == null) return;

    const update = (): void => setMinWidth(root.offsetWidth);
    update();

    const ro = new ResizeObserver(update);
    ro.observe(root);
    return (): void => ro.disconnect();
  }, []);

  return (
    <ASelect
      ref={selectRef}
      className={join(styles.select, props.className)}
      disabled={disabled}
      loading={loading}
      value={selected}
      mode='multiple'
      maxTagCount={maxTagCount}
      showSearch
      allowClear={allowClear}
      placeholder={placeholder}
      filterOption={filterOption}
      searchValue={keyWords}
      autoClearSearchValue={false}
      onSelect={(value): void => {
        if (value !== SELECT_ALL) {
          const newSelected = [...selected, value];
          const isAllSelected = filterOptions.every(item => newSelected.includes(item.value));
          if (isAllSelected) {
            setSelected([SELECT_ALL, ...selected, value]);
            setKeyWords('');
          } else {
            setSelected(newSelected);
          }
          onChange(newSelected.filter(item => item !== SELECT_ALL));
          return;
        }
        setSelected([SELECT_ALL, ...filterOptions.map(item => item.value)]);
        onChange(filterOptions.map(item => item.value));
        setKeyWords('');
      }}
      onDeselect={(value): void => {
        if (value !== SELECT_ALL) {
          const newSelected = selected.filter(item => item !== value && item !== SELECT_ALL);
          setSelected(newSelected);

          onChange(newSelected);
          return;
        }
        const newSelected
          = selected.filter(item => !filterOptions.map(item => item.value).includes(item) && item !== SELECT_ALL);
        setSelected(newSelected);
        onChange(newSelected);
        setKeyWords('');
      }}
      onClear={(): void => {
        setKeyWords('');
        setSelected([]);
        onChange([]);
      }}
      labelRender={labelRender}
      onSearch={onSearch}
      popupClassName={styles.selectPopup}
      dropdownStyle={md === false ? { minWidth, maxWidth: 300 } : { minWidth, maxWidth: 600 }}
      dropdownRender={props.dropdownRender}
      options={selectOptions}
      optionRender={optionRender}
      onDropdownVisibleChange={visible => {
        if (!visible) {
          setKeyWords('');
        }
      }}
    />
  );
}

/**
 * 搜索过滤
 */
function filterOption(input: string, option?: SelectOption): boolean {
  return (option?.label ?? '').toLowerCase().includes(input.toLowerCase()) || option?.value === 'all';
}
